import { useMemo, useCallback } from 'react';
import { useTabbedSearch } from '../../../hooks/useTabbedSearch';
import { search } from '../../../services/practice-collection-service';
import { readingPracticeRequestsService } from '../../../services/request-service';

export const usePracticeCollectionSearch = () => {
  const requestsTab = 0;
  const draftTab = 1;
  const publishTab = 2;
  const approvedTab = 3;
  const releasedTab = 4;
  const archivedTab = 5;

  const tabsConfig = useMemo(() => [
    { id: requestsTab, status: 'IN_PROGRESS' },
    { id: draftTab, status: 'DRAFT' },
    { id: publishTab, status: 'PUBLISHED' },
    { id: approvedTab, status: 'APPROVED' },
    { id: releasedTab, status: 'RELEASED' },
    { id: archivedTab, status: 'ARCHIVED' },
  ], []);

  const fetchData = useCallback(async ({ tabId, status, searchTerm, pageNum, pageSize }) => {
    if (tabId === requestsTab) {
      const result = await readingPracticeRequestsService.searchPracticeCollectionRequests({
        term: searchTerm,
        pageNumber: pageNum,
        pageSize,
        statuses: ['PENDING', 'GENERATING_OUTLINE', 'GENERATING_PRACTICES', 'MANUAL_REVIEW', 'CREATING_COLLECTION', 'FAILED'],
      });
      return {
        items: result.items || [],
        hasNext: result.hasNext || false,
      };
    }

    const result = await search({
      term: searchTerm,
      pageNumber: pageNum,
      pageSize,
      statuses: [status],
      types: ['FICTION', 'NON_FICTION', 'PATHWAY'],
    });
    return {
      items: result.items || [],
      hasNext: result.hasNext || false,
    };
  }, []);

  const {
    loading,
    error,
    searchTerm,
    viewType,
    tabValue,
    showCreateForm,
    hasMore,
    submitSearchParams,
    setShowCreateForm,
    loadMore,
    handleTabChange,
    handleViewTypeChange,
    getTabContents,
    refreshTab,
  } = useTabbedSearch({
    tabs: tabsConfig,
    initialTabValue: publishTab,
    pageSize: 10,
    fetchDataFn: fetchData,
    cacheKey: 'practice-collections',
  });

  const handleContentGenerated = useCallback(() => {
    refreshTab(requestsTab);
    refreshTab(draftTab);
    setShowCreateForm(false);
  }, [refreshTab, setShowCreateForm, draftTab, requestsTab]);

  return {
    loading,
    error,
    searchTerm,
    viewType,
    tabValue,
    showCreateForm,
    hasMore,
    submitSearchParams,
    setShowCreateForm,
    loadMore,
    handleTabChange,
    handleViewTypeChange,
    handleContentGenerated,
    getTabContents,
    refreshTab,
    requestsTab,
    draftTab,
    publishTab,
    approvedTab,
    releasedTab,
    archivedTab,
  };
};
